import {
  IEditCommand,
  IEditCommandFactory,
  IEditEntityCommandStorable,
  AddValueToArrayCommand,
} from "./command";
import { ISourceReferable } from "./ISourceReferable";
import { SourceReader } from "./SourceReader";
import { IPropertyPresentable } from "./IPropertyPresentable";
import { IPropertyEditable } from "./IPropertyEditable";
import { PropertyPresenter } from "./PropertyPresenter";
import { PropertyEditor } from "./PropertyEditor";
import { IPropertyPresenterCreatable } from "./IPropertyPresenterCreatable";
import { IPropertyEditorCreatable } from "./IPropertyEditorCreatable";

export abstract class AbstractAddEntityCommandFactory<TSource, TEntityBase, TEntity extends TEntityBase> implements IEditCommandFactory<TSource>, IEditEntityCommandStorable<TEntity>, ISourceReferable<TEntity>, IPropertyPresenterCreatable<TEntity>, IPropertyEditorCreatable<TEntity> {
  constructor(source: TEntity | AbstractAddEntityCommandFactory<TSource, TEntityBase, TEntity>) {
    this._entity = (source instanceof AbstractAddEntityCommandFactory)
      ? source._entity
      : new SourceReader(source);
  }

  private readonly _entity: SourceReader<TEntity>;

  public getSource(): TEntity {
    return this._entity.getSource();
  }

  public isReferedSameSource(instance: ISourceReferable<TEntity>): boolean {
    return (this.getSource() === instance.getSource());
  }

  protected get<T>(get: (s: TEntity) => T, defaultValue: T): T {
    return this._entity.getOrDefault(get, defaultValue);
  }

  public createCommand(): IEditCommand<TSource> {
    return new AddValueToArrayCommand(s => this.toEntities(s), this._entity._source);
  }

  protected abstract toEntities(source: TSource): TEntityBase[];

  public registerEntityCommand(command: IEditCommand<TEntity>): void {
    command.do(this._entity._source);
  }

  public createPropertyPresenter<T>(get: (s: TEntity) => T): IPropertyPresentable<T> {
    return new PropertyPresenter({
      source: this._entity._source,
      read: get,
    });
  }

  public createPropertyEditor<T>(get: (s: TEntity) => T, set: (s: TEntity, v: T) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<T> {
    return new PropertyEditor({
      source: this._entity._source,
      read: get,
      write: v => set(this._entity._source, v),
      isWritable: isSettable,
    });
  }

  public createPropertiesEditor<TValue, TValues>(get: (s: TEntity) => TValues, toValue: (vs: TValues) => TValue, set: (s: TEntity, v: TValue) => void, revert: (s: TEntity, vs: TValues) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<TValue> {
    return new PropertyEditor({
      source: this._entity._source,
      read: s => toValue(get(s)),
      write: v => set(this._entity._source, v),
      isWritable: isSettable,
    });
  }
}
